import React from 'react'
import { X, FileText, Shield, AlertTriangle, Info } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'

interface TermsModalProps {
  isOpen: boolean
  onClose: () => void
  onAccept: () => void
}

const TermsModal: React.FC<TermsModalProps> = ({ isOpen, onClose, onAccept }) => {
  const handleAccept = () => {
    onAccept()
    onClose()
  }

  if (!isOpen) return null

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="relative w-full max-w-4xl max-h-[90vh] bg-white dark:bg-gray-800 rounded-2xl shadow-2xl overflow-hidden flex flex-col"
        >
          
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-blue-600 rounded-lg">
                <FileText className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Termos de Uso e Política de Privacidade
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  SENAI - Vitrine de Projetos Acadêmicos
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content Area */}
          <div
            className="flex-1 overflow-y-auto p-6 space-y-6"
          >
            
                                    
            {/* Alerta Importante */}
            <div className="bg-yellow-50 dark:bg-yellow-900/30 border-2 border-yellow-500 dark:border-yellow-600 rounded-xl p-5">
              <div className="flex gap-3">
                <AlertTriangle className="w-6 h-6 text-yellow-700 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-bold text-yellow-900 dark:text-yellow-100 text-lg mb-2">
                    Leia atentamente antes de prosseguir
                  </h3>
                  <p className="text-sm text-yellow-800 dark:text-yellow-200 leading-relaxed">
                    Ao aceitar estes termos, você concorda com as condições de uso da plataforma e com as regras de propriedade intelectual aplicáveis aos projetos cadastrados.
                  </p>
                </div>
              </div>
            </div>

            {/* Seção 1 */}
            <section className="space-y-4">
              <div className="flex items-center gap-2 border-b border-gray-200 dark:border-gray-700 pb-2">
                <Shield className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                  1. TERMOS DE USO DA PLATAFORMA
                </h3>
              </div>
              
              <div className="text-gray-600 dark:text-gray-300 space-y-4 text-justify leading-relaxed">
                <div>
                  <h4 className="font-bold text-gray-900 dark:text-gray-100 mb-1">1.1 Aceitação dos Termos</h4>
                  <p>Ao utilizar a plataforma "Vitrine Tecnológica" do SENAI Feira de Santana, você declara que leu, compreendeu e concorda integralmente com estes Termos de Uso e com a Política de Privacidade.</p>
                </div>
                
                <div>
                  <h4 className="font-bold text-gray-900 dark:text-gray-100 mb-1">1.2 Descrição da Plataforma</h4>
                  <p>A "Vitrine Tecnológica" é uma plataforma digital destinada ao cadastro, armazenamento, compartilhamento e divulgação de projetos desenvolvidos por alunos no contexto educacional do SENAI. Seu objetivo é estimular a inovação, o empreendedorismo e a continuidade de projetos acadêmicos.</p>
                </div>

                <div>
                  <h4 className="font-bold text-gray-900 dark:text-gray-100 mb-1">1.3 Usuários Elegíveis</h4>
                  <p>A plataforma é destinada a:</p>
                  <ul className="list-disc pl-5 mt-2 space-y-1">
                    <li>Alunos matriculados</li>
                    <li>Instrutores e orientadores de projetos</li>
                    <li>Equipe pedagógica e administrativa</li>
                    <li>Usuários externos com acesso restrito à visualização, conforme configurações de privacidade</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-bold text-gray-900 dark:text-gray-100 mb-1">1.4 Obrigações do Usuário</h4>
                  <p>Ao utilizar a plataforma, o usuário se compromete a:</p>
                  <ul className="list-disc pl-5 mt-2 space-y-1">
                    <li>Fornecer informações verdadeiras e atualizadas</li>
                    <li>Respeitar direitos de propriedade intelectual</li>
                    <li>Utilizar a plataforma exclusivamente para fins educacionais</li>
                    <li>Não publicar conteúdo ilegal, ofensivo ou discriminatório</li>
                    <li>Não utilizar a plataforma para fins comerciais sem autorização institucional</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Seção 2 */}
            <section className="space-y-4">
              <div className="flex items-center gap-2 border-b border-gray-200 dark:border-gray-700 pb-2">
                <FileText className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                  2. PROPRIEDADE INTELECTUAL E DIREITOS AUTORAIS
                </h3>
              </div>
              
              <div className="text-gray-600 dark:text-gray-300 space-y-4 text-justify leading-relaxed">
                <div>
                  <h4 className="font-bold text-gray-900 dark:text-gray-100 mb-1">2.1 Titularidade e Cessão de Direitos</h4>
                  <p>Ao publicar um projeto, o usuário declara estar ciente de que:</p>
                  <ul className="list-disc pl-5 mt-2 space-y-1">
                    <li>O projeto estará vinculado às diretrizes institucionais de inovação do SENAI/DR/BA</li>
                    <li>Poderá haver cessão de direitos ao SENAI, conforme normativos institucionais e natureza do projeto</li>
                    <li>O SENAI poderá utilizar o projeto para fins educacionais, institucionais e de inovação</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-bold text-gray-900 dark:text-gray-100 mb-1">2.2 Direitos Morais</h4>
                  <p>O autor mantém os direitos morais sobre a obra, incluindo:</p>
                  <ul className="list-disc pl-5 mt-2 space-y-1">
                    <li>Reconhecimento de autoria</li>
                    <li>Vinculação do nome ao projeto</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-bold text-gray-900 dark:text-gray-100 mb-1">2.3 Continuidade de Projetos</h4>
                  <p>Projetos cadastrados poderão:</p>
                  <ul className="list-disc pl-5 mt-2 space-y-1">
                    <li>Ser aprimorados em novos ciclos acadêmicos</li>
                    <li>Ter coautoria reconhecida em caso de evolução do projeto</li>
                  </ul>
                  <p className="mt-2">Essa prática está alinhada ao estímulo à inovação, colaboração e desenvolvimento tecnológico institucional.</p>
                </div>

                <div>
                  <h4 className="font-bold text-gray-900 dark:text-gray-100 mb-1">2.4 Uso Institucional dos Projetos</h4>
                  <p>Os projetos poderão ser utilizados pelo SENAI para:</p>
                  <ul className="list-disc pl-5 mt-2 space-y-1">
                    <li>Atividades pedagógicas</li>
                    <li>Eventos e feiras</li>
                    <li>Divulgação institucional</li>
                    <li>Desenvolvimento de soluções educacionais e tecnológicas</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-bold text-gray-900 dark:text-gray-100 mb-1">2.5 Responsabilidade do Conteúdo</h4>
                  <p>O usuário declara que:</p>
                  <ul className="list-disc pl-5 mt-2 space-y-1">
                    <li>É autor do projeto ou possui autorização para publicação</li>
                    <li>O conteúdo não viola direitos de terceiros</li>
                    <li>Não há uso indevido de marcas, patentes ou informações confidenciais</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Seção 3 */}
            <section className="space-y-4">
              <div className="flex items-center gap-2 border-b border-gray-200 dark:border-gray-700 pb-2">
                <Shield className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                  3. POLÍTICA DE PRIVACIDADE E PROTEÇÃO DE DADOS (LGPD)
                </h3>
              </div>
              
              <div className="text-gray-600 dark:text-gray-300 space-y-4 text-justify leading-relaxed">
                <div>
                  <h4 className="font-bold text-gray-900 dark:text-gray-100 mb-1">3.1 Conformidade Legal</h4>
                  <p>O tratamento de dados segue a Lei nº 13.709/2018 (LGPD).</p>
                </div>

                <div>
                  <h4 className="font-bold text-gray-900 dark:text-gray-100 mb-1">3.2 Dados Coletados</h4>
                  <p>Podem ser coletados:</p>
                  <ul className="list-disc pl-5 mt-2 space-y-1">
                    <li>Dados de identificação (nome, telefone, etc.)</li>
                    <li>Dados acadêmicos (curso, turma, unidade curricular)</li>
                    <li>Dados do projeto (descrição, arquivos, equipe)</li>
                    <li>Dados de navegação (logs, IP, cookies)</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-bold text-gray-900 dark:text-gray-100 mb-1">3.3 Finalidade do Uso dos Dados</h4>
                  <p>Os dados são utilizados para:</p>
                  <ul className="list-disc pl-5 mt-2 space-y-1">
                    <li>Gestão dos projetos acadêmicos</li>
                    <li>Acompanhamento pedagógico</li>
                    <li>Comunicação institucional</li>
                    <li>Geração de indicadores educacionais</li>
                    <li>Melhoria da plataforma</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-bold text-gray-900 dark:text-gray-100 mb-1">3.4 Base Legal</h4>
                  <p>O tratamento ocorre com base em:</p>
                  <ul className="list-disc pl-5 mt-2 space-y-1">
                    <li>Consentimento do usuário</li>
                    <li>Execução de atividades educacionais</li>
                    <li>Interesse legítimo institucional</li>
                    <li>Cumprimento de obrigações legais</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-bold text-gray-900 dark:text-gray-100 mb-1">3.5 Compartilhamento de Dados</h4>
                  <p>Os dados podem ser compartilhados com:</p>
                  <ul className="list-disc pl-5 mt-2 space-y-1">
                    <li>Unidades do SENAI</li>
                    <li>Parceiros institucionais</li>
                    <li>Órgãos reguladores</li>
                    <li>Projetos e iniciativas vinculadas à inovação</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-bold text-gray-900 dark:text-gray-100 mb-1">3.6 Armazenamento e Segurança</h4>
                  <p>O SENAI adota medidas para garantir:</p>
                  <ul className="list-disc pl-5 mt-2 space-y-1">
                    <li>Segurança das informações</li>
                    <li>Controle de acesso</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-bold text-gray-900 dark:text-gray-100 mb-1">3.7 Retenção dos Dados</h4>
                  <p>Os dados poderão ser mantidos:</p>
                  <ul className="list-disc pl-5 mt-2 space-y-1">
                    <li>Durante o vínculo acadêmico</li>
                    <li>Para fins históricos e educacionais</li>
                    <li>Conforme exigências legais</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Seção 4 */}
            <section className="space-y-4">
              <div className="flex items-center gap-2 border-b border-gray-200 dark:border-gray-700 pb-2">
                <Info className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                  4. DIREITOS DO TITULAR DOS DADOS
                </h3>
              </div>
              
              <div className="text-gray-600 dark:text-gray-300 space-y-4 text-justify leading-relaxed">
                <p>Nos termos da LGPD, o usuário pode solicitar:</p>
                <ul className="list-disc pl-5 mt-2 space-y-1">
                  <li>Acesso aos seus dados</li>
                  <li>Correção de informações</li>
                  <li>Exclusão de dados (somente quando aplicável)</li>
                  <li>Revogação do consentimento</li>
                </ul>
                <p>Solicitações podem ser feitas pelos canais institucionais do SENAI.</p>
              </div>
            </section>

            {/* Seção 5 */}
            <section className="space-y-4">
              <div className="flex items-center gap-2 border-b border-gray-200 dark:border-gray-700 pb-2">
                <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                  5. DISPOSIÇÕES GERAIS
                </h3>
              </div>
              
              <div className="text-gray-600 dark:text-gray-300 space-y-4 text-justify leading-relaxed">
                <div>
                  <h4 className="font-bold text-gray-900 dark:text-gray-100 mb-1">5.1 Atualizações dos Termos</h4>
                  <p>Os termos podem ser atualizados a qualquer momento, com comunicação na plataforma.</p>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 dark:text-gray-100 mb-1">5.2 Uso Indevido</h4>
                  <p>O descumprimento destes termos pode resultar em:</p>
                  <ul className="list-disc pl-5 mt-2 space-y-1">
                    <li>Suspensão de acesso</li>
                    <li>Exclusão de conteúdo</li>
                    <li>Outras medidas institucionais</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 dark:text-gray-100 mb-1">5.3 Legislação Aplicável</h4>
                  <p>Este documento é regido pela legislação brasileira vigente.</p>
                </div>
              </div>
            </section>
</div>

          {/* Footer */}
          <div className="p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 space-y-4">
            
            {/* Botões */}
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="flex-1 px-6 py-3 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-xl font-medium transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleAccept}
                className="flex-1 px-6 py-3 rounded-xl font-bold transition-all bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white shadow-lg"
              >
                Aceitar Termos
              </button>
            </div>

            <p className="text-xs text-center text-gray-500 dark:text-gray-400">
              Ao aceitar, você concorda com todos os termos e autoriza o tratamento de seus dados conforme descrito.
            </p>
          </div>

        </motion.div>
      </div>
    </AnimatePresence>
  )
}

export default TermsModal
